import { exitReasonLabel, getStatusLabel, normalizeStatus } from '@/utils/statusTransitions';
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Client } from '@/types/shelter';
import { History, LogIn, LogOut, BedDouble, Utensils, CreditCard, Coffee, Sunset, CheckCircle, FileText, Mail, Bell, CalendarIcon, UserPlus, Edit, ArrowRightLeft, Hospital } from 'lucide-react';
import { format } from 'date-fns';
import { formatClientFieldList } from '@/lib/clientFieldLabels';

interface ServiceRecord {
  id: string;
  type: 'transit_card' | 'laundry_card' | 'meal' | 'beverage' | 'breakfast' | 'lunch' | 'dinner' | 'milk' | 'juice';
  description: string;
  quantity?: number;
  mealType?: 'breakfast' | 'lunch' | 'dinner';
  timestamp: string;
  staffMember: string;
}

interface ActivityLog {
  id: string;
  action_type: string;
  action_details: any;
  created_at: string;
  profiles?: { full_name: string };
}

interface CheckinSession {
  id: string;
  checkedInAt: string;
  checkedOutAt: string | null;
  staffName: string;
}

interface StatusEventRow {
  id: string;
  from_status: string | null;
  to_status: string | null;
  exit_reason: string | null;
  created_at: string;
  staffName?: string;
}

interface CaseManagerHistoryRow {
  id: string;
  from_name?: string | null;
  to_name?: string | null;
  changed_at: string;
  staffName?: string;
}

interface ClientProfileHistoryProps {
  client: Client;
  activityLogs: ActivityLog[];
  serviceRecords: ServiceRecord[];
  checkinSessions?: CheckinSession[];
}

const getMealIcon = (mealType?: string) => {
  switch (mealType) {
    case 'breakfast': return <Coffee className="h-4 w-4" />;
    case 'lunch': return <Utensils className="h-4 w-4" />;
    case 'dinner': return <Sunset className="h-4 w-4" />;
    default: return <Utensils className="h-4 w-4" />;
  }
};

const getServiceIcon = (type: ServiceRecord['type']) => {
  switch (type) {
    case 'transit_card': return <CreditCard className="h-4 w-4" />;
    case 'laundry_card': return <CreditCard className="h-4 w-4" />;
    case 'meal': case 'lunch': case 'dinner': return <Utensils className="h-4 w-4" />;
    case 'breakfast': return <Coffee className="h-4 w-4" />;
    case 'beverage': return <Coffee className="h-4 w-4" />;
    case 'milk': case 'juice': return <Coffee className="h-4 w-4" />;
    default: return <Utensils className="h-4 w-4" />;
  }
};

const getServiceTypeColor = (type: ServiceRecord['type']) => {
  switch (type) {
    case 'transit_card': return 'bg-blue-100 text-blue-800';
    case 'laundry_card': return 'bg-purple-100 text-purple-800';
    case 'meal': case 'breakfast': case 'lunch': case 'dinner': return 'bg-green-100 text-green-800';
    case 'beverage': case 'milk': case 'juice': return 'bg-orange-100 text-orange-800';
    default: return 'bg-gray-100 text-gray-800';
  }
};

interface TimelineEvent {
  timestamp: string;
  type: string;
  description: string;
  icon: React.ReactNode;
  color: string;
  staffName?: string;
}

const isNearSessionEvent = (timestamp: string, type: 'check-in' | 'check-out', sessions: CheckinSession[]) => {
  const eventTime = new Date(timestamp).getTime();
  if (Number.isNaN(eventTime)) return false;

  return sessions.some((session) => {
    const sessionTimestamp = type === 'check-in' ? session.checkedInAt : session.checkedOutAt;
    if (!sessionTimestamp) return false;
    return Math.abs(new Date(sessionTimestamp).getTime() - eventTime) <= 120000;
  });
};

const getMatchingActivityStaff = (timestamp: string, actionType: 'check_in' | 'check_out', activityLogs: ActivityLog[]) => {
  const eventTime = new Date(timestamp).getTime();
  if (Number.isNaN(eventTime)) return undefined;

  const matchingLog = activityLogs.find((log) => {
    if (log.action_type !== actionType) return false;
    const details = log.action_details as any;
    const logTime = new Date(details?.timestamp || log.created_at).getTime();
    return !Number.isNaN(logTime) && Math.abs(logTime - eventTime) <= 120000;
  });

  return matchingLog?.profiles?.full_name;
};

const humanizeActionType = (actionType: string): string =>
  String(actionType || 'Activity')
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (m) => m.toUpperCase());

const buildEvents = (
  activityLogs: ActivityLog[],
  serviceRecords: ServiceRecord[],
  checkinSessions: CheckinSession[] = [],
  statusEvents: StatusEventRow[] = [],
  caseManagerHistory: CaseManagerHistoryRow[] = [],
): TimelineEvent[] => {
  const allEvents: TimelineEvent[] = [];

  checkinSessions.forEach((session) => {
    allEvents.push({
      timestamp: session.checkedInAt,
      type: 'check-in',
      description: 'Checked In',
      icon: <LogIn className="h-4 w-4" />,
      color: 'bg-success text-success-foreground',
      staffName: getMatchingActivityStaff(session.checkedInAt, 'check_in', activityLogs) || session.staffName
    });

    if (session.checkedOutAt) {
      allEvents.push({
        timestamp: session.checkedOutAt,
        type: 'check-out',
        description: 'Checked Out',
        icon: <LogOut className="h-4 w-4" />,
        color: 'bg-muted text-muted-foreground',
        staffName: getMatchingActivityStaff(session.checkedOutAt, 'check_out', activityLogs) || session.staffName
      });
    }
  });

  activityLogs.forEach((log) => {
    const details = log.action_details as any;
    const staffName = log.profiles?.full_name || 'Staff';
    const eventTimestamp = details?.timestamp || log.created_at;

    if (log.action_type === 'check_in') {
      if (!isNearSessionEvent(eventTimestamp, 'check-in', checkinSessions)) {
        allEvents.push({ timestamp: eventTimestamp, type: 'check-in', description: 'Checked In', icon: <LogIn className="h-4 w-4" />, color: 'bg-success text-success-foreground', staffName });
      }
    } else if (log.action_type === 'check_out') {
      if (!isNearSessionEvent(eventTimestamp, 'check-out', checkinSessions)) {
        allEvents.push({ timestamp: eventTimestamp, type: 'check-out', description: 'Checked Out', icon: <LogOut className="h-4 w-4" />, color: 'bg-muted text-muted-foreground', staffName });
      }
    } else if (log.action_type === 'update_client' && details?.new_status === 'unbedded') {
      allEvents.push({ timestamp: details?.timestamp || log.created_at, type: 'status-change', description: 'Unbedded', icon: <BedDouble className="h-4 w-4" />, color: 'bg-amber-100 text-amber-700', staffName });
    } else if (log.action_type === 'acknowledge_reminder') {
      allEvents.push({ timestamp: details?.timestamp || log.created_at, type: 'reminder-acknowledge', description: `Reminder Acknowledged: "${details?.reminder_text || 'Unknown'}"`, icon: <CheckCircle className="h-4 w-4" />, color: 'bg-teal-100 text-teal-800', staffName });
    } else if (log.action_type === 'create_note') {
      allEvents.push({ timestamp: details?.timestamp || log.created_at, type: 'note', description: `Note Added: "${details?.note_title || 'Untitled'}"`, icon: <FileText className="h-4 w-4" />, color: 'bg-blue-100 text-blue-800', staffName });
    } else if (log.action_type === 'acknowledge_alert') {
      const alertTitle = details?.resolvedTitle || details?.alertTitle || 'Alert';
      const isMailRelated = alertTitle.toLowerCase().includes('mail') || alertTitle.toLowerCase().includes('package');
      allEvents.push({ timestamp: details?.timestamp || log.created_at, type: 'update', description: isMailRelated ? `${alertTitle}` : `Alert Resolved: "${alertTitle}"`, icon: isMailRelated ? <Mail className="h-4 w-4" /> : <Bell className="h-4 w-4" />, color: isMailRelated ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800', staffName });
    } else if (log.action_type === 'create_client') {
      allEvents.push({ timestamp: details?.timestamp || log.created_at, type: 'intake', description: 'New Intake', icon: <UserPlus className="h-4 w-4" />, color: 'bg-primary/10 text-primary', staffName });
    } else if (log.action_type === 'client_status_change') {
      // The database status-event trigger is the authoritative source; skip the
      // duplicate application log when trigger rows exist.
      if (statusEvents.length > 0) return;
      const toStatusRaw = details?.new_status || details?.to_status || details?.to || 'unknown';
      const toStatus = String(toStatusRaw).toLowerCase();
      const isUnbedded = toStatus === 'unbedded';
      const label = getStatusLabel(normalizeStatus(toStatus) as any) || toStatusRaw;
      allEvents.push({
        timestamp: details?.timestamp || log.created_at,
        type: 'status-change',
        description: isUnbedded ? 'Unbedded' : `Status Changed to ${label}`,
        icon: isUnbedded ? <BedDouble className="h-4 w-4" /> : <ArrowRightLeft className="h-4 w-4" />,
        color: 'bg-amber-100 text-amber-700',
        staffName,
      });
    } else if (log.action_type === 'update_client' && !details?.new_status) {
      let desc = 'Profile Updated';
      if (details?.updates && Array.isArray(details.updates) && details.updates.length > 0) {
        const friendly = formatClientFieldList(details.updates as string[]);
        if (friendly.length > 0) desc = `Profile Updated (${friendly.join(', ')})`;
      }
      allEvents.push({ timestamp: details?.timestamp || log.created_at, type: 'update', description: desc, icon: <Edit className="h-4 w-4" />, color: 'bg-muted text-muted-foreground', staffName });
    } else if (log.action_type === 'assign_pass') {
      allEvents.push({ timestamp: details?.timestamp || log.created_at, type: 'pass', description: `Pass Assigned (${details?.pass_start ? new Date(details.pass_start).toLocaleDateString() : ''} – ${details?.pass_end ? new Date(details.pass_end).toLocaleDateString() : ''})`, icon: <CalendarIcon className="h-4 w-4" />, color: 'bg-purple-100 text-purple-800', staffName });
    } else if (log.action_type === 'update_pass') {
      allEvents.push({ timestamp: details?.timestamp || log.created_at, type: 'pass', description: `Pass Updated (${details?.pass_start ? new Date(details.pass_start).toLocaleString() : ''} – ${details?.pass_end ? new Date(details.pass_end).toLocaleString() : ''})`, icon: <CalendarIcon className="h-4 w-4" />, color: 'bg-secondary text-secondary-foreground', staffName });
    } else if (log.action_type === 'clear_pass') {
      allEvents.push({ timestamp: details?.timestamp || log.created_at, type: 'pass', description: 'Pass Cleared', icon: <CalendarIcon className="h-4 w-4" />, color: 'bg-muted text-muted-foreground', staffName });
    } else if (log.action_type === 'room_assignment') {
      const roomNum = details?.room_number || 'unknown';
      allEvents.push({ timestamp: details?.timestamp || log.created_at, type: 'room', description: details?.room_id ? `Room Assigned: ${roomNum}` : 'Room Unassigned', icon: <BedDouble className="h-4 w-4" />, color: 'bg-blue-100 text-blue-800', staffName });
    } else if (log.action_type === 'property_return') {
      allEvents.push({ timestamp: details?.timestamp || log.created_at, type: 'property', description: 'Property Returned to Client', icon: <CheckCircle className="h-4 w-4" />, color: 'bg-green-100 text-green-800', staffName });
    } else if (log.action_type === 'exit_client') {
      allEvents.push({ timestamp: details?.timestamp || log.created_at, type: 'exit', description: `Exited${details?.exit_reason ? ` — ${exitReasonLabel(details.exit_reason)}` : ''}`, icon: <LogOut className="h-4 w-4" />, color: 'bg-muted text-muted-foreground', staffName });
    } else if (log.action_type === 're_intake_client') {
      allEvents.push({ timestamp: details?.timestamp || log.created_at, type: 'intake', description: 'Re-Intake', icon: <UserPlus className="h-4 w-4" />, color: 'bg-primary/10 text-primary', staffName });
    } else if (log.action_type === 'release_reservation') {
      allEvents.push({ timestamp: details?.timestamp || log.created_at, type: 'room', description: 'Reservation Released (bed freed)', icon: <BedDouble className="h-4 w-4" />, color: 'bg-blue-100 text-blue-800', staffName });
    } else {
      // No event is silently dropped — unmapped action types get a readable fallback.
      allEvents.push({
        timestamp: details?.timestamp || log.created_at,
        type: 'other',
        description: humanizeActionType(log.action_type),
        icon: <ArrowRightLeft className="h-4 w-4" />,
        color: 'bg-muted text-muted-foreground',
        staffName,
      });
    }
  });

  statusEvents.forEach((ev) => {
    const label = ev.to_status ? getStatusLabel(normalizeStatus(ev.to_status) as any) : 'Updated';
    allEvents.push({
      timestamp: ev.created_at,
      type: 'status-change',
      description: ev.exit_reason
        ? `Exited — ${exitReasonLabel(ev.exit_reason)}`
        : ev.from_status
          ? `Status Changed to ${label}`
          : `Intake — ${label}`,
      icon: <ArrowRightLeft className="h-4 w-4" />,
      color: 'bg-amber-100 text-amber-700',
      staffName: ev.staffName || 'System',
    });
  });

  caseManagerHistory.forEach((h) => {
    allEvents.push({
      timestamp: h.changed_at,
      type: 'case-manager',
      description: h.to_name
        ? `Case Manager ${h.from_name ? `changed from ${h.from_name} to ${h.to_name}` : `assigned: ${h.to_name}`}`
        : 'Case Manager Cleared',
      icon: <UserPlus className="h-4 w-4" />,
      color: 'bg-secondary text-secondary-foreground',
      staffName: h.staffName || 'System',
    });
  });

  serviceRecords.forEach(record => {
    const friendlyType: Record<string, string> = {
      transit_card: 'Transit',
      laundry_card: 'Laundry Card',
      meal: 'Meal',
      beverage: 'Beverage',
      breakfast: 'Breakfast',
      lunch: 'Lunch',
      dinner: 'Dinner',
      milk: 'Milk',
      juice: 'Juice'
    };
    const label = friendlyType[record.type] || record.type;
    const mealLabel = record.mealType ? ` — ${record.mealType.charAt(0).toUpperCase() + record.mealType.slice(1)}` : '';
    const qtyLabel = record.quantity && record.quantity > 1 ? ` × ${record.quantity}` : '';
    allEvents.push({
      timestamp: record.timestamp,
      type: 'service',
      description: `${label}${mealLabel}${qtyLabel}`,
      icon: record.type === 'meal' ? getMealIcon(record.mealType) : getServiceIcon(record.type),
      color: getServiceTypeColor(record.type),
      staffName: record.staffMember
    });
  });

  allEvents.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
  return allEvents;
};

export const ClientProfileHistory: React.FC<ClientProfileHistoryProps> = ({
  client,
  activityLogs,
  serviceRecords,
  checkinSessions = []
}) => {
  const { data: statusEvents = [] } = useQuery({
    queryKey: ['client-status-events', client.id],
    enabled: !!client.id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('client_status_events')
        .select('id, previous_status, new_status, exit_reason, changed_at, changed_by, profiles:changed_by(full_name)')
        .eq('client_id', client.id)
        .order('changed_at', { ascending: false });
      if (error) return [] as StatusEventRow[];
      return (data || []).map((r: any) => ({
        id: r.id,
        from_status: r.previous_status,
        to_status: r.new_status,
        exit_reason: r.exit_reason,
        created_at: r.changed_at,
        staffName: r.profiles?.full_name || 'System',
      })) as StatusEventRow[];
    },
  });

  const { data: caseManagerHistory = [] } = useQuery({
    queryKey: ['client-case-manager-history', client.id],
    enabled: !!client.id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('client_case_manager_history')
        .select('id, created_at, from_user_id, to_user_id, changed_by')
        .eq('client_id', client.id)
        .order('created_at', { ascending: false });
      if (error) return [] as CaseManagerHistoryRow[];
      const ids = Array.from(new Set((data || []).flatMap((r: any) => [r.from_user_id, r.to_user_id, r.changed_by]).filter(Boolean)));
      let names: Record<string, string> = {};
      if (ids.length) {
        const { data: profs } = await supabase.from('profiles').select('id, full_name').in('id', ids);
        names = Object.fromEntries((profs || []).map((p: any) => [p.id, p.full_name]));
      }
      return (data || []).map((r: any) => ({
        id: r.id,
        from_name: r.from_user_id ? names[r.from_user_id] || 'Unknown' : null,
        to_name: r.to_user_id ? names[r.to_user_id] || 'Unknown' : null,
        changed_at: r.created_at,
        staffName: r.changed_by ? names[r.changed_by] || 'System' : 'System',
      })) as CaseManagerHistoryRow[];
    },
  });


  const allEvents = buildEvents(activityLogs, serviceRecords, checkinSessions, statusEvents, caseManagerHistory);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <CardTitle className="flex items-center gap-2">
              <History className="h-5 w-5 text-primary" />
              Activity Log
            </CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              Most recent first
            </p>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {allEvents.length > 0 ? (
          <div className="relative">
            <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-border" />
            <div className="space-y-4">
              {allEvents.map((event, idx) => (
                <div key={idx} className="flex items-start gap-4 relative">
                  <div className={`relative z-10 p-2 rounded-full ${event.color} flex-shrink-0`}>
                    {event.icon}
                  </div>
                  <div className="flex-1 pt-1">
                    <p className="font-medium text-sm">{event.description}</p>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
                      <span>
                        {format(new Date(event.timestamp), 'MMM d, yyyy h:mm a')}
                      </span>
                      {event.staffName && (
                        <>
                          <span>•</span>
                          <span>by {event.staffName}</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            <History className="h-12 w-12 mx-auto mb-2 opacity-50" />
            <p>No activity recorded for this client</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};